import { getActivityBySelfUrlRequest } from "./getActivityBySelfUrl.request";
import type { Activity } from "./types";
export type ParamsType = Record<string, unknown>;

const isComplete = (activity: Activity) => activity?.completion_percent >= 100;

/**
 * Wait for the activity to complete.
 *
 * @param function  onPoll
 * A function that will be called every time the activity is polled for updates. It will be passed one argument: the Activity object.
 * @param function  onLog
 * A function that will print new activity log messages as they are received. It will be passed one argument: the message as a string.
 * @param int float pollInterval The polling interval, in seconds.
 */
export const waitOnActivity = (
  activity: Activity,
  onPoll?: (activity: Activity) => void,
  onLog?: (log: string) => void,
  pollInterval = 1
): Promise<Activity> => {
  let retries = 0;
  let logLength = activity?.log?.length ?? 0;
  const initialLog = activity?.log || "";

  if (onLog && initialLog.trim().length) {
    onLog(`${initialLog.trim()}\n`);
  }

  const poll = (
    currentActivity: Activity,
    resolve: (a: Activity) => void,
    reject: (e: unknown) => void
  ) => {
    if (isComplete(currentActivity)) {
      return resolve(currentActivity);
    }

    getActivityBySelfUrlRequest(currentActivity, { timeout: pollInterval + 5 })
      .then(updatedActivity => {
        if (!updatedActivity?.id) {
          throw new Error("Invalid activity response payload.");
        }
        if (onPoll) {
          onPoll(updatedActivity);
        }

        if (onLog) {
          const currentLog = updatedActivity.log || "";

          const newLogSegment = currentLog.substring(logLength);

          if (newLogSegment.trim().length > 0) {
            onLog(`${newLogSegment.trim()}\n`);
          }
          logLength = currentLog.length;
        }
        // Schedule the next poll
        setTimeout(
          () => poll(updatedActivity, resolve, reject),
          pollInterval * 1000
        );
      })
      .catch(err => {
        if (err.message.indexOf("cURL error 28") !== -1 && retries < 5) {
          retries++;
          return setTimeout(() => poll(currentActivity, resolve, reject), 0);
        }
        reject(err);
      });
  };
  return new Promise<Activity>((resolve, reject) => {
    // Start the recursive polling process
    poll(activity, resolve, reject);
  });
};
